import {useCallback, useMemo} from "react";
import {useDynamicMenuItemContext} from "../contexts/DynamicMenuItemContext";
import {CollapsibleMenu, MenuItem, MenuLink} from "./MobileMenu/MenuTypes";

export default function useDynamicMenuItems(menuItems: MenuItem[]) {
	const dynamicValues = useDynamicMenuItemContext();
	const makeDynamicMenuItem = useCallback(
		(menuItem: MenuItem): MenuItem => {
			// if the item is a nested submenu, recurse
			if ("menuItems" in menuItem) {
				const {menuItems: submenuItems, ...submenuProperties} =
					menuItem as CollapsibleMenu;
				const dynamicSubmenuItems = submenuItems.map((item) =>
					makeDynamicMenuItem(item)
				);
				return {
					...submenuProperties,
					menuItems: dynamicSubmenuItems,
				};
			}

			// if the item is a static menu link, build out the dynamicHref
			const {dynamicHref, ...staticMenuItem} = menuItem as MenuLink;
			if (!dynamicHref || !dynamicValues) {
				return menuItem;
			}
			const dynamicSegments = dynamicHref.match(/:[^/]+/g) ?? [];
			// Only populate href if all dynamic values have finished loading and exist
			if (
				dynamicSegments.every((segment) => {
					const key = segment.slice(1);
					return (
						!dynamicValues[key]?.isLoading &&
						dynamicValues[key]?.value !== undefined
					);
				})
			) {
				const populatedHref = dynamicSegments.reduce((acc, segment) => {
					const key = segment.slice(1);
					const value = dynamicValues[key].value;
					// technically the || "" isn't necessary because we've already
					// checked for existence.  But typescript doesn't know that.
					// and if typescript doesn't know, we don't know. ovo;
					return acc.replace(segment, value || "");
				}, dynamicHref);

				return {
					...staticMenuItem,
					href: populatedHref,
				};
			}
			return menuItem;
		},
		[dynamicValues]
	);

	const dynamicMenuItems = useMemo(
		() =>
			menuItems.map((menuItem) => {
				return makeDynamicMenuItem(menuItem);
			}),
		[menuItems, makeDynamicMenuItem]
	);

	return dynamicMenuItems;
}
